import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, TrendingUp, BarChart3, Activity, Zap, ArrowUp, ArrowDown, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SignalCard } from '@/components/stock/signal-card';
import { SignalIndicator } from '@/components/stock/signal-indicator';
import { S1SignalChart } from '@/components/stock/s1-signal-chart';
import { useStockQuote, useStockHistory, useDetailedSignal, useStockProfile } from '@/lib/hooks/use-stock';
import { useSearchHistory } from '@/lib/hooks/use-search-history';

export default function StockDetailPage() {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const ticker = params.ticker as string;
  const [showMA, setShowMA] = useState(true);

  const { data: quote, isLoading: quoteLoading, error: quoteError } = useStockQuote(ticker);
  const { data: history, isLoading: historyLoading } = useStockHistory(ticker, '1d', '3mo');
  const { data: signal, isLoading: signalLoading, error: signalError } = useDetailedSignal(ticker);
  const { data: profile } = useStockProfile(ticker);
  const { addToHistory } = useSearchHistory();

  // 格式化市值：英文用B(十亿)，中文用亿
  const formatMarketCap = (marketCap: number) => {
    const divisor = i18n.language === 'zh' ? 1e8 : 1e9;
    return (marketCap / divisor).toFixed(2) + t('stock.marketCapUnit');
  };

  // 数据加载成功后保存到搜索历史
  useEffect(() => {
    if (quote && signal && !quoteError && !signalError) {
      addToHistory(quote.symbol, quote.name);
    }
  }, [quote, signal, quoteError, signalError, addToHistory]);

  if (quoteLoading || signalLoading || historyLoading) {
    return (
      <div className="flex items-center justify-center pt-32">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">{t('stock.loadingData')}</p>
        </div>
      </div>
    );
  }

  if (quoteError || signalError) {
    return (
      <div className="flex items-center justify-center pt-32">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('stock.errorLoadingData')}</h2>
          <p className="text-slate-600 mb-4">
            {t('stock.errorMessage', { ticker })}
          </p>
          <Button onClick={() => navigate('/')}>{t('stock.backToHome')}</Button>
        </div>
      </div>
    );
  }

  if (!quote || !signal) {
    return null;
  }

  const actionColors = {
    BUY: 'success',
    HOLD: 'warning',
    SELL: 'destructive',
  } as const;

  const strengthTextMap = {
    1: 'strongBearish',
    2: 'bearish',
    3: 'neutral',
    4: 'bullish',
    5: 'strongBullish',
  } as const;

  const signalName = t(`signal.${strengthTextMap[signal.overview.overallStrength]}`);
  const summaryText = t('analysis.summaryTemplate', {
    signal: signalName,
    confidence: signal.overview.confidence,
  });

  const isPositive = quote.change >= 0;

  return (
    <div className="pt-20">
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('stock.backToHome')}
        </Button>

        {/* Stock Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{quote.symbol}</h1>
              <p className="text-slate-600">{quote.name}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-slate-900">${quote.price.toFixed(2)}</div>
              <div className={`flex items-center justify-end gap-1 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                <span>
                  {isPositive ? '+' : ''}
                  {quote.change.toFixed(2)} ({isPositive ? '+' : ''}
                  {quote.changePercent.toFixed(2)}%)
                </span>
              </div>
              
              {/* 52 Week High */}
              {profile?.fiftyTwoWeekHigh && (
                <div className="mt-1 text-xs text-slate-500">
                  <span className="font-medium">{t('stock.fiftyTwoWeekHigh')}:</span>
                  <span className="ml-1">${profile.fiftyTwoWeekHigh.toFixed(2)}</span>
                </div>
              )}

              {/* All Time High */}
              {profile?.allTimeHigh && (
                <div className="mt-1 text-xs text-slate-500">
                  <span className="font-medium">{t('stock.allTimeHigh')}:</span>
                  <span className="ml-1">${profile.allTimeHigh.toFixed(2)}</span>
                  {profile.allTimeHighDate && (
                    <span className="ml-1 text-slate-400">
                      ({new Date(profile.allTimeHighDate).getFullYear()})
                    </span>
                  )}
                </div>
              )}

              {/* Pre-market / Post-market Price */}
              {quote.preMarketPrice && (
                <div className="mt-2 text-xs text-slate-500">
                  <span className="font-medium">{t('stock.preMarket')}:</span>
                  <span className="ml-1">${quote.preMarketPrice.toFixed(2)}</span>
                  <span className={`ml-1 ${quote.preMarketChange && quote.preMarketChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {quote.preMarketChange && quote.preMarketChange >= 0 ? '+' : ''}
                    {quote.preMarketChange?.toFixed(2)} ({quote.preMarketChangePercent && quote.preMarketChangePercent >= 0 ? '+' : ''}
                    {quote.preMarketChangePercent?.toFixed(2)}%)
                  </span>
                </div>
              )}
              
              {quote.postMarketPrice && !quote.preMarketPrice && (
                <div className="mt-2 text-xs text-slate-500">
                  <span className="font-medium">{t('stock.postMarket')}:</span>
                  <span className="ml-1">${quote.postMarketPrice.toFixed(2)}</span>
                  <span className={`ml-1 ${quote.postMarketChange && quote.postMarketChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {quote.postMarketChange && quote.postMarketChange >= 0 ? '+' : ''}
                    {quote.postMarketChange?.toFixed(2)} ({quote.postMarketChangePercent && quote.postMarketChangePercent >= 0 ? '+' : ''}
                    {quote.postMarketChangePercent?.toFixed(2)}%)
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Company Profile */}
        {profile && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t('stock.profile')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700 mb-4">{profile.description}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500 block">{t('stock.sector')}</span>
                  <span className="font-medium">{profile.sector || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">{t('stock.industry')}</span>
                  <span className="font-medium">{profile.industry || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">{t('stock.marketCap')}</span>
                  <span className="font-medium">{profile.marketCap ? formatMarketCap(profile.marketCap) : '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">{t('stock.employees')}</span>
                  <span className="font-medium">{profile.employees?.toLocaleString() || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">{t('stock.peRatio')}</span>
                  <span className="font-medium">{profile.peRatio?.toFixed(2) || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">{t('stock.dividendYield')}</span>
                  <span className="font-medium">{profile.dividendYield ? (profile.dividendYield * 100).toFixed(2) + '%' : '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">{t('stock.beta')}</span>
                  <span className="font-medium">{profile.beta?.toFixed(2) || '-'}</span>
                </div>
                {profile.website && (
                  <div>
                    <span className="text-slate-500 block">{t('stock.website')}</span>
                    <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block">
                      {profile.website}
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Signal Overview */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('stock.signalDetails')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-slate-600 mb-2">{t('index.signalStrength')}</p>
                <SignalIndicator strength={signal.overview.overallStrength} size="lg" />
              </div>
              <div className="group relative">
                <div className="flex items-center gap-1 mb-2 cursor-help">
                  <p className="text-sm text-slate-600">{t('index.action')}</p>
                  <HelpCircle className="h-3 w-3 text-slate-400" />
                </div>
                <Badge variant={actionColors[signal.overview.action]} className="text-lg px-4 py-2">
                  {t(`signal.${signal.overview.action.toLowerCase()}`)}
                </Badge>

                {/* Action Tooltip */}
                <div className="absolute bottom-full mb-2 left-0 hidden group-hover:block w-72 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-xl z-50 pointer-events-none">
                  <div className="relative">
                    {t('index.actionDescription')}
                    <div className="absolute top-full left-4 border-8 border-transparent border-t-slate-800"></div>
                  </div>
                </div>
              </div>
              <div className="group relative">
                <div className="flex items-center gap-1 mb-2 cursor-help">
                  <p className="text-sm text-slate-600">{t('index.confidence')}</p>
                  <HelpCircle className="h-3 w-3 text-slate-400" />
                </div>
                <div className="text-2xl font-bold text-slate-900">{signal.overview.confidence}%</div>
                
                {/* Confidence Tooltip */}
                <div className="absolute bottom-full mb-2 left-0 hidden group-hover:block w-72 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-xl z-50 pointer-events-none">
                  <div className="relative">
                    {t('index.confidenceDescription')}
                    <div className="absolute top-full left-4 border-8 border-transparent border-t-slate-800"></div>
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-4 text-slate-700">{summaryText}</p>
          </CardContent>
        </Card>

        {/* Signal Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <SignalCard
            title={t('stock.trendSignal')}
            icon={<TrendingUp className="h-5 w-5 text-blue-600" />}
            strength={signal.trend.strength}
            analysis={signal.trend.analysis}
            details={
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.shortTerm')}:</span>
                  <span className="font-semibold capitalize">{t(`analysis.${signal.trend.shortTerm.toLowerCase()}`)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.mediumTerm')}:</span>
                  <span className="font-semibold capitalize">{t(`analysis.${signal.trend.mediumTerm.toLowerCase()}`)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.longTerm')}:</span>
                  <span className="font-semibold capitalize">{t(`analysis.${signal.trend.longTerm.toLowerCase()}`)}</span>
                </div>
              </div>
            }
          />

          <SignalCard
            title={t('stock.volumeSignal')}
            icon={<BarChart3 className="h-5 w-5 text-green-600" />}
            strength={signal.volume.strength}
            analysis={signal.volume.analysis}
            details={
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.currentVolume')}:</span>
                  <span className="font-semibold">{(signal.volume.currentVolume / 1e6).toFixed(2)}M</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.avgVolume20d')}:</span>
                  <span className="font-semibold">{(signal.volume.avgVolume20d / 1e6).toFixed(2)}M</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.volumeRatio')}:</span>
                  <span className="font-semibold">{signal.volume.volumeRatio.toFixed(2)}x</span>
                </div>
              </div>
            }
          />

          <SignalCard
            title={t('stock.momentumSignal')}
            icon={<Activity className="h-5 w-5 text-purple-600" />}
            strength={signal.momentum.strength}
            analysis={signal.momentum.analysis}
            details={
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.rsi')}:</span>
                  <span className="font-semibold">{signal.momentum.rsi.toFixed(1)} ({t(`analysis.${signal.momentum.rsiStatus.toLowerCase()}`)})</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.macd')}:</span>
                  <span className="font-semibold capitalize">{t(`analysis.${signal.momentum.macd.trend.toLowerCase()}`)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.stochastic')}:</span>
                  <span className="font-semibold">{signal.momentum.stochastic.k.toFixed(1)}</span>
                </div>
              </div>
            }
          />

          <SignalCard
            title={t('stock.volatilitySignal')}
            icon={<Zap className="h-5 w-5 text-orange-600" />}
            strength={signal.volatility.strength}
            analysis={signal.volatility.analysis}
            details={
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.atr')}:</span>
                  <span className="font-semibold">{signal.volatility.atrPercent.toFixed(2)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.volatilityLevel')}:</span>
                  <span className="font-semibold capitalize">{t(`analysis.${signal.volatility.volatilityLevel.toLowerCase()}`)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{t('stock.bbPosition')}:</span>
                  <span className="font-semibold capitalize">{t(`analysis.${signal.volatility.bollingerBands.position.toLowerCase()}`)}</span>
                </div>
              </div>
            }
          />
        </div>

        {/* S1 Signal Chart */}
        {history && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="flex items-center gap-2">
                  {t('stock.priceChart3m')}
                  <span className="text-sm font-normal text-slate-500">S1 Signal</span>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">MA5/MA20</span>
                  <Switch checked={showMA} onCheckedChange={setShowMA} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <S1SignalChart history={history} showSignals={true} showMA={showMA} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
